"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { DICT } from "./dictionary";

export type Locale = "en" | "fr";

const STORAGE_KEY = "woozoo.lang";

// Collapse internal whitespace exactly like the original i18n.js lookup key.
function normalize(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

type TFn = (en: string) => string;

type I18nContextType = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  t: TFn;
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({ children }: { children: ReactNode }) {
  // Default EN — matches the mockup, which only swaps to FR on toggle.
  const [locale, setLocaleState] = useState<Locale>("en");

  // Pick the initial language on mount. Priority:
  //   1. a previously chosen language (localStorage)
  //   2. an explicit ?lang= override (used by the app's WebView screens)
  //   3. the browser's preferred language (navigator.languages)
  //   4. fall back to EN
  // Kept in an effect (not the initializer) to avoid an SSR/client hydration
  // mismatch — the server always renders EN, the client adjusts once.
  useEffect(() => {
    const detect = (): Locale | null => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === "fr" || stored === "en") return stored;
      } catch {
        /* ignore */
      }
      const param = new URLSearchParams(window.location.search).get("lang");
      if (param === "fr" || param === "en") return param;
      const langs = navigator.languages?.length
        ? navigator.languages
        : [navigator.language];
      if (langs.some((l) => l?.toLowerCase().startsWith("fr"))) return "fr";
      return null;
    };

    const next = detect();
    if (next && next !== "en") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocaleState(next);
    }
    if (next) document.documentElement.lang = next;
  }, []);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    document.documentElement.lang = l;
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleLocale = useCallback(() => {
    setLocale(locale === "fr" ? "en" : "fr");
  }, [locale, setLocale]);

  const t = useMemo<TFn>(() => {
    if (locale === "en") return (en: string) => en;
    return (en: string) => DICT[normalize(en)] ?? en;
  }, [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, toggleLocale, t }),
    [locale, setLocale, toggleLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
